import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { delay, formatDateTimeLocal, formattedDate, formattedDateTime, ToastMessage, useDebounce } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { useBulkDeletePatientVisitsMutation, useBulkDestroyPatientVisitsMutation, useBulkRestorePatientVisitsMutation, useDeletePatientVisitMutation, useDestroyPatientVisitMutation, useFetchPatientVisitsQuery, useFetchTrashedPatientVisitsQuery, useRestorePatientVisitMutation } from "../../../stores/api/patient.visit.api";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const PatientVisitList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patient_id = location?.state?.patient_id;



  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const debouncedSearch  = useDebounce(search,500)
  const queryParams = payloadHelper.forList({
    page,
    limit,
    patient_id: patient_id,
    search: debouncedSearch,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchPatientVisitsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedPatientVisitsQuery(queryParams, { skip: !trashed });
  const { data: units = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deletePatientVisit] = useDeletePatientVisitMutation();
  const [destroyPatientVisit] = useDestroyPatientVisitMutation();
  const [restorePatientVisit] = useRestorePatientVisitMutation();
  const [bulkdeletePatientVisits] = useBulkDeletePatientVisitsMutation();
  const [bulkdestroyPatientVisits] = useBulkDestroyPatientVisitsMutation();
  const [bulkrestorePatientVisits] = useBulkRestorePatientVisitsMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this Patient Visit?`,
      title: `Patient Visit ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restorePatientVisit(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyPatientVisit(row.id).unwrap();
          } else {
            await deletePatientVisit(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `Patient Visit ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} Patient Visit: ${row?.patient?.patient_name}`,
          });
        } catch (err) {
          handleError(err, "Failed to update user.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Patient Visit?`,
      title: `Patient Visit ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkrestorePatientVisits(body).unwrap();
          } else if (type === "destroy") {
            await bulkdestroyPatientVisits(body).unwrap();
          } else {
            await bulkdeletePatientVisits(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected Patient Visit`,
            message: `${selectedItems.length} Patient Visits ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    { key: "patient.system_number", label: "Patient-ID / CNIC", sortable: true , render: (row) => (
      <div className="text-xs whitespace-nowrap">
        {row?.patient?.system_number ||  row?.patient?.patient_cnic}
      </div>)
    },
    { key: "patient.patient_name", label: "Patient Name", sortable: true },
    { key: "visit_date", label: "Visit Date & Time", sortable: true , render: (row) => (
      <div className="text-xs whitespace-nowrap">
        {formattedDateTime(row.visit_date)}
      </div>)
    },
    { key: "attendant.name", label: "Attendant", sortable: true },
    { key: "next_visit_plan", label: "Next Planned Date & Time", sortable: true , render: (row) => (
      <div className="text-xs whitespace-nowrap">
        {formattedDateTime(row.next_visit_plan)}
      </div>)
    },
    {
      key: "status",
      label: "Status",
      type: "status",
    },
  ];
  
  const renderActions = (row) => (
    <div className="flex gap-2">
      {!trashed ? (
        <Button
          onClick={() => navigate(ROUTE_PATHS.PATIENT_VISIT_CREATE, { state: { patient_visit: row } })}
          title="Edit"
          icon={Edit3Icon}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcw}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      )}
      <Button
        onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
        title="Delete"
        icon={TrashIcon}
        className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Patient Visits" />
      <TableListing
        headers={headers}
        paginationData={units}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.PATIENT_VISIT_CREATE}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        statusModule={"patient_visit"}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default PatientVisitList;
